import { Box, Input as InputNimbus } from '@nimbus-ds/components';
import { useTranslation } from 'react-i18next';
import { Domain, AiFeature } from '@tiendanube/common/src/enums';
import { FEATURE_AI_TIER } from 'App/features';
import { CATALOG_PRODUCT_HASHED_SLUG } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { Input } from 'commons/components';
import GenerateWithAiLink from 'commons/components/GenerateWithAiLink';
import {
  useTranslationCommon,
  type AiNavigationAttributes,
} from 'commons/hooks';
import { useAiUsage } from 'domains/Ai/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import type { AIGeneratedType } from 'domains/Catalog/Products/productsSlice';
import TagAndSeoItemTitle from '../TagAndSeoItemTitle';

export type onChangeBrandType = ({
  name,
  value,
}: {
  name: string;
  value: string;
}) => void;

interface BrandProps {
  value: string;
  disabled: boolean;
  aiGenerated: boolean;
  missingNameAndDescription: boolean;
  onChange: onChangeBrandType;
  onClickGenerateWithAI: (type: AIGeneratedType) => void;
  aiNavigationAttributes?: AiNavigationAttributes;
}

function Brand({
  value,
  disabled,
  aiGenerated,
  missingNameAndDescription,
  onChange,
  onClickGenerateWithAI,
  aiNavigationAttributes,
}: Readonly<BrandProps>): JSX.Element {
  const { t } = useTranslation(Domain.CATALOG);
  const tCommon = useTranslationCommon();
  const { requiredTier, aiUsesUpsellTitle } = useAiUsage();
  const [hasCatalogProductHashedSlug] = useHasTags([
    CATALOG_PRODUCT_HASHED_SLUG,
  ]);

  const getTooltipText = () => {
    if (value.trim() !== '') return tCommon('aiGeneration.alreadyExists.brand');
    if (missingNameAndDescription)
      return tCommon('aiGeneration.addNameAndDescriptionToGenerate');
    return '';
  };
  const tooltipText = getTooltipText();

  const upsellBrand = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_PRODUCT_BRAND,
    callback: () => onClickGenerateWithAI('brand'),
    minValue: requiredTier,
    asAside: !hasCatalogProductHashedSlug,
  });

  const label = hasCatalogProductHashedSlug
    ? ''
    : t('products.detail.brand.name');

  const description = hasCatalogProductHashedSlug
    ? t('products.detail.brand.description')
    : '';

  const ariaLabel = label ? '' : description;

  const generateWithAiLink = (
    <GenerateWithAiLink
      onClick={upsellBrand}
      fontSize="caption"
      trackingFeature={AiFeature.AI_PRODUCT_BRAND}
      disabled={!!tooltipText}
      tooltipText={tooltipText}
    />
  );

  return (
    <TagAndSeoItemTitle
      title={t('products.detail.brand.title')}
      description={description}
      slot={generateWithAiLink}
      aiNavigationAttributes={aiNavigationAttributes}
    >
      {disabled ? (
        <Box width="100%">
          <InputNimbus.Skeleton width="100%" />
        </Box>
      ) : (
        <Input
          label={label}
          placeholder={t('products.detail.brand.placeholder')}
          onChange={onChange}
          value={value}
          name="brand"
          disabled={disabled}
          aiGenerated={!disabled && aiGenerated}
          aria-label={ariaLabel}
        />
      )}
    </TagAndSeoItemTitle>
  );
}

export default Brand;
